/**
 * CorrectionPlanCreatedCard - Displays correction plan for fixing issues
 * 
 * Shows the correction context, fixes with detailed instructions, and constraints.
 */
import { cn } from '@/lib/utils'
import { AlertTriangle, FileCode, ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'

export interface CorrectionFix {
  fix_id: string
  original_plan_item: string
  issue_type: string
  goal: string
  files_to_modify: string[]
  specific_instructions: string
  complexity?: string
}

export interface CorrectionPlanCreatedData {
  correction_context: string
  original_plan_id?: string
  fixes: CorrectionFix[]
  constraints?: string[]
}

export interface CorrectionPlanCreatedCardProps {
  data: CorrectionPlanCreatedData
  reasoning?: string
  className?: string
}

function getComplexityColor(complexity?: string): string {
  switch (complexity?.toLowerCase()) {
    case 'low':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    case 'medium':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    case 'high':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
  }
}

function getIssueTypeColor(issueType: string): string {
  switch (issueType.toLowerCase()) {
    case 'incorrectly_implemented':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
    case 'missing':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    case 'regression':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
  }
}

function FixItem({ fix, index }: { fix: CorrectionFix; index: number }) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <li className="border rounded-lg overflow-hidden bg-muted/30">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button 
            variant="ghost" 
            className="w-full justify-start px-3 py-3 h-auto hover:bg-muted/50"
          >
            <div className="flex items-start gap-3 w-full">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-500/20 text-orange-600 dark:text-orange-400 text-xs font-medium flex items-center justify-center mt-0.5">
                {index + 1}
              </span>
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <Badge variant="outline" className={cn("text-xs", getIssueTypeColor(fix.issue_type))}>
                    {fix.issue_type.replace(/_/g, ' ')}
                  </Badge>
                  {fix.complexity && (
                    <Badge variant="outline" className={cn("text-xs", getComplexityColor(fix.complexity))}>
                      {fix.complexity}
                    </Badge>
                  )}
                </div>
                <p className="text-sm font-medium">{fix.goal}</p>
                {fix.original_plan_item && (
                  <p className="text-xs text-muted-foreground mt-1">Original: {fix.original_plan_item}</p>
                )}
              </div>
              {isOpen ? (
                <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              )}
            </div>
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-3 pb-3 pt-1 space-y-3 border-t bg-background/50">
            {/* Files to modify */}
            {fix.files_to_modify && fix.files_to_modify.length > 0 && (
              <div>
                <h5 className="text-xs font-medium text-muted-foreground mb-1">Files to modify</h5>
                <div className="flex flex-wrap gap-1">
                  {fix.files_to_modify.map((file, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs font-mono">
                      <FileCode className="h-3 w-3 mr-1" />
                      {file}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Specific instructions */}
            {fix.specific_instructions && (
              <div>
                <h5 className="text-xs font-medium text-muted-foreground mb-1">Instructions</h5>
                <pre className="text-xs whitespace-pre-wrap bg-muted p-2 rounded font-mono overflow-x-auto">
                  {fix.specific_instructions}
                </pre>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </li>
  )
}

export function CorrectionPlanCreatedCard({ data, reasoning, className }: CorrectionPlanCreatedCardProps) {
  const { correction_context, original_plan_id, fixes, constraints } = data
  
  return (
    <div className={cn("rounded-lg border border-orange-200 dark:border-orange-800 bg-card p-4 space-y-4", className)}>
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-full bg-orange-100 dark:bg-orange-900">
          <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base text-orange-700 dark:text-orange-300">Correction Plan Created</h3>
          <p className="text-sm text-muted-foreground mt-1">{correction_context}</p>
          {original_plan_id && (
            <p className="text-xs text-muted-foreground mt-1">Original plan: {original_plan_id}</p>
          )}
        </div>
      </div>
      
      {/* Fixes */}
      {fixes && fixes.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">Fixes ({fixes.length})</h4>
          <ol className="space-y-2">
            {fixes.map((fix, idx) => (
              <FixItem key={fix.fix_id || idx} fix={fix} index={idx} />
            ))}
          </ol>
        </div>
      )}
      
      {/* Constraints */}
      {constraints && constraints.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">Constraints</h4>
          <div className="flex flex-wrap gap-1">
            {constraints.map((constraint, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {constraint}
              </Badge>
            ))}
          </div>
        </div>
      )}
      
      {/* Reasoning */}
      {reasoning && (
        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground">{reasoning}</p>
        </div>
      )}
    </div>
  )
}
